import React, { createContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { isSupported, setup } from '@loomhq/record-sdk'
import { connect } from 'react-redux'
import {
  isCurrentOrginizationMentorLoomRecordingEnabled,
  isCurrentOrginizationStudentLoomRecordingEnabled
} from '../selectors/organization'
import {
  checkHttpStatus,
  getApiBaseUrlWithOrg,
  getApiHeadersFromToken
} from 'utils/api'
import withApiData from 'components/HOC/withApiData'

export const LoomContext = createContext({
  enabled: false,
  supported: null,
  notSupportedReasons: null,
  loom: null
})

const fetchLoomJws = async apiData => {
  const url = `${getApiBaseUrlWithOrg(apiData.orgId)}/loom/jwt`
  try {
    const response = checkHttpStatus(
      await fetch(url, {
        method: 'GET',
        headers: getApiHeadersFromToken(apiData.token)
      })
    )
    const data = await response.json()
    return data.token
  } catch (error) {
    return null
  }
}

// Link to loomSDK docs
// https://dev.loom.com/docs/record-sdk/details/api
const LoomProvider = ({
  apiData,
  children,
  isMentorLoomRecordingEnabled,
  isStudentLoomRecordingEnabled
}) => {
  const [supported, setSupported] = useState(null)
  const [notSupportedReasons, setNotSupportedReasons] = useState(null)
  const [loom, setLoom] = useState(null)

  const enabled = apiData && (
    apiData.isMentor && isMentorLoomRecordingEnabled ||
    !apiData.isMentor && isStudentLoomRecordingEnabled
  )

  useEffect(() => {
    if (enabled) {
      (async () => {
        const supported = await isSupported()
        setSupported(supported.supported)
        setNotSupportedReasons(supported.error)
      })()
    }
  }, [enabled])

  useEffect(() => {
    if (enabled && supported && !loom) {
      (async () => {
        const jws = await fetchLoomJws(apiData)
        if (jws) {
          const l = await setup({ jws })
          if (l.status().success) {
            setLoom(l)
          } else {
            setSupported(false)
          }
        } else {
          setSupported(false)
        }
      })()
    } else if (!enabled && loom) {
      loom.teardown()
      setLoom(null)
    }
  }, [enabled, supported, loom])


  useEffect(() => {
    return () => {
      if (loom) {
        loom.teardown()
      }
    }
  }, [])

  const value = {
    enabled,
    supported,
    notSupportedReasons,
    loom
  }

  return (
    <LoomContext.Provider value={value}>
      {children}
    </LoomContext.Provider>
  )
}

const mapStateToProps = state => ({
  isMentorLoomRecordingEnabled:
    isCurrentOrginizationMentorLoomRecordingEnabled(state),
  isStudentLoomRecordingEnabled:
    isCurrentOrginizationStudentLoomRecordingEnabled(state)
})

LoomProvider.propTypes = {
  apiData: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  isMentorLoomRecordingEnabled: PropTypes.bool,
  isStudentLoomRecordingEnabled: PropTypes.bool
}

export default connect(mapStateToProps)(withApiData(LoomProvider))
